import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Camera, 
  Calendar, 
  Home, 
  User, 
  Download, 
  ChevronLeft, 
  ChevronRight,
  X,
  Loader2,
  ImageIcon,
  Filter,
  Clock,
  Eye,
  ArrowLeft,
  FileText,
  Share2,
  Link,
  Copy,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhotoGallery = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [gallery, setGallery] = useState({ items: [], total: 0, page: 1, total_pages: 1 });
  const [stats, setStats] = useState({ tasks_with_photos: 0, total_photos: 0 });
  const [filters, setFilters] = useState({ properties: [], housekeepers: [] });
  
  // Filter state
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [propertyId, setPropertyId] = useState('all');
  const [housekeeperId, setHousekeeperId] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxTask, setLightboxTask] = useState(null);
  const [photoMetadata, setPhotoMetadata] = useState({});
  const [loadingMetadata, setLoadingMetadata] = useState(false);
  
  // Share state
  const [sharingTaskId, setSharingTaskId] = useState(null);
  const [copiedTaskId, setCopiedTaskId] = useState(null);

  // Share gallery function
  const handleShareGallery = async (task, e) => {
    e.stopPropagation();
    // Use task_id for gallery items, or id for task details
    const taskId = task.task_id || task.id;
    setSharingTaskId(taskId);
    try {
      // Check if share link exists
      let response = await fetch(`${API_URL}/api/tasks/${taskId}/share-gallery`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      let data = await response.json();
      
      // Create if not exists
      if (!data.share_token) {
        response = await fetch(`${API_URL}/api/tasks/${taskId}/share-gallery`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        data = await response.json();
      }
      
      if (data.share_token) {
        const shareUrl = `${window.location.origin}/gallery/${data.share_token}`;
        await navigator.clipboard.writeText(shareUrl);
        setCopiedTaskId(taskId);
        toast.success('Lien copié ! Envoyez-le au propriétaire.');
        setTimeout(() => setCopiedTaskId(null), 3000);
      }
    } catch (error) {
      toast.error('Erreur lors de la création du lien');
    } finally {
      setSharingTaskId(null);
    }
  };

  const fetchFilters = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/gallery/filters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setFilters(data);
      }
    } catch (error) {
      console.error('Error fetching filters:', error);
    }
  }, [token]);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      if (propertyId && propertyId !== 'all') params.append('property_id', propertyId);
      if (housekeeperId && housekeeperId !== 'all') params.append('housekeeper_id', housekeeperId);
      params.append('page', currentPage);
      params.append('limit', 20);

      const res = await fetch(`${API_URL}/api/gallery/photos?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setGallery(data);
      }
    } catch (error) {
      console.error('Error fetching gallery:', error);
      toast.error('Erreur lors du chargement de la galerie');
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo, propertyId, housekeeperId, currentPage]);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const res = await fetch(`${API_URL}/api/gallery/stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token, dateFrom, dateTo]);

  // Fetch filters on mount
  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  // Fetch gallery when filters change
  useEffect(() => {
    fetchGallery();
    fetchStats();
  }, [fetchGallery, fetchStats]);

  const applyQuickFilter = (period) => {
    const today = new Date();
    switch (period) {
      case 'today':
        setDateFrom(format(today, 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
        break;
      case 'week':
        setDateFrom(format(subDays(today, 7), 'yyyy-MM-dd'));
        setDateTo(format(today, 'yyyy-MM-dd'));
        break;
      case 'month':
        setDateFrom(format(startOfMonth(today), 'yyyy-MM-dd'));
        setDateTo(format(endOfMonth(today), 'yyyy-MM-dd'));
        break;
      case 'lastMonth':
        const lastMonth = subMonths(today, 1);
        setDateFrom(format(startOfMonth(lastMonth), 'yyyy-MM-dd'));
        setDateTo(format(endOfMonth(lastMonth), 'yyyy-MM-dd'));
        break;
      case 'all':
        setDateFrom('');
        setDateTo('');
        break;
    }
    setCurrentPage(1);
  };

  const openLightbox = async (task, photoIndex = 0) => {
    setLightboxTask(task);
    setLightboxPhotos(task.photos);
    setLightboxIndex(photoIndex);
    setLightboxOpen(true);
    
    // Use task_id from gallery data or id from task detail
    const taskId = task.task_id || task.id;
    
    // Charger les métadonnées GPS si pas déjà chargées
    if (!photoMetadata[taskId]) {
      setLoadingMetadata(true);
      try {
        const res = await fetch(`${API_URL}/api/tasks/${taskId}/photos/metadata`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Indexer par storage_path pour accès rapide
          const indexed = {};
          data.forEach(photo => {
            indexed[photo.storage_path] = photo;
          });
          setPhotoMetadata(prev => ({ ...prev, [taskId]: indexed }));
        }
      } catch (error) {
        console.error('Error fetching photo metadata:', error);
      } finally {
        setLoadingMetadata(false);
      }
    }
  };

  // Obtenir les infos GPS d'une photo
  const getPhotoGpsInfo = (taskId, photoPath) => {
    const taskMeta = photoMetadata[taskId];
    if (!taskMeta) return null;
    const photoMeta = taskMeta[photoPath];
    return photoMeta?.gps_info || null;
  };

  // Obtenir toutes les métadonnées d'une photo (incluant is_keybox_photo)
  const getPhotoMetadata = (taskId, photoPath) => {
    const taskMeta = photoMetadata[taskId];
    if (!taskMeta) return null;
    return taskMeta[photoPath] || null;
  };

  const getPhotoUrl = (path) => {
    return `${API_URL}/api/photo?path=${encodeURIComponent(path)}&auth=${token}`;
  };

  const downloadPhoto = async (path, filename) => {
    try {
      const response = await fetch(getPhotoUrl(path));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'photo.jpg';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Photo téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const downloadAllPhotos = async (task) => {
    toast.info(`Téléchargement de ${task.photos.length} photos...`);
    for (let i = 0; i < task.photos.length; i++) {
      const photo = task.photos[i];
      const filename = `${task.property_name}_${task.date}_photo${i + 1}.jpg`;
      await downloadPhoto(photo, filename);
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
    }
    toast.success('Toutes les photos ont été téléchargées');
  };

  // Download PDF report with certificate
  const downloadPdfReport = async (task) => {
    toast.info('Génération du rapport PDF...');
    try {
      // Use task_id from gallery data or id from task detail
      const taskId = task.task_id || task.id;
      const response = await fetch(`${API_URL}/api/tasks/${taskId}/report-pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        // Try to get error message from response
        let errorMessage = 'Erreur lors de la génération du PDF';
        try {
          const errorData = await response.clone().json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // Response wasn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rapport_${task.property_name}_${task.date}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Rapport PDF téléchargé avec succès');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error(error.message || 'Erreur lors du téléchargement du PDF');
    }
  };

  const formatTimeSpent = (minutes) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h${mins.toString().padStart(2, '0')}`;
    }
    return `${mins} min`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1F3A8A] to-blue-700 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate(-1)}
              className="text-white hover:bg-white/20 -ml-2"
              data-testid="gallery-back-btn"
            >
              <ArrowLeft className="h-6 w-6" />
            </Button>
            <Camera className="h-8 w-8" />
            <h1 className="text-2xl font-bold">Galerie Photos</h1>
          </div>
          <p className="text-blue-100 ml-12">Consultez toutes les photos de fin de prestation</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <ImageIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total photos</p>
                <p className="text-2xl font-bold text-slate-800">{stats.total_photos}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <Camera className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Prestations</p>
                <p className="text-2xl font-bold text-slate-800">{stats.tasks_with_photos}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filtres
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Quick filters */}
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => applyQuickFilter('today')}
                className={dateFrom === format(new Date(), 'yyyy-MM-dd') ? 'bg-blue-50 border-blue-300' : ''}
              >
                Aujourd&apos;hui
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => applyQuickFilter('week')}
              >
                7 derniers jours
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => applyQuickFilter('month')}
              >
                Ce mois
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => applyQuickFilter('lastMonth')}
              >
                Mois dernier
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => applyQuickFilter('all')}
              >
                Tout
              </Button>
            </div>

            {/* Date filters */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs text-slate-500">Date début</Label>
                <Input 
                  type="date" 
                  value={dateFrom} 
                  onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Date fin</Label>
                <Input 
                  type="date" 
                  value={dateTo} 
                  onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Logement</Label>
                <Select value={propertyId} onValueChange={(v) => { setPropertyId(v); setCurrentPage(1); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Tous les logements" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les logements</SelectItem>
                    {filters.properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Femme de ménage</Label>
                <Select value={housekeeperId} onValueChange={(v) => { setHousekeeperId(v); setCurrentPage(1); }}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Toutes les FDM" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les FDM</SelectItem>
                    {filters.housekeepers.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Gallery Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : gallery.items.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <ImageIcon className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 font-medium">Aucune photo trouvée</p>
              <p className="text-slate-400 text-sm mt-1">Modifiez les filtres pour voir plus de résultats</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {gallery.items.map((task) => {
              const displayPhotos = task.photos.slice(0, 3);
              const remainingCount = task.photos.length - 3;
              
              return (
                <Card 
                  key={task.task_id} 
                  className="border-0 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden group cursor-pointer flex flex-col"
                  onClick={() => openLightbox(task, 0)}
                >
                  {/* Task Info - EN HAUT pour toujours être visible */}
                  <CardContent className="p-4 flex-shrink-0 border-b bg-white">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-slate-800 truncate flex items-center gap-2">
                          <Home className="h-4 w-4 text-blue-600 flex-shrink-0" />
                          {task.property_name || 'Logement'}
                        </h3>
                        <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            {task.date ? format(new Date(task.date), "d MMM yyyy", { locale: fr }) : 'Date inconnue'}
                          </span>
                          {task.housekeeper_names && task.housekeeper_names.length > 0 && (
                            <>
                              <span className="text-slate-300">•</span>
                              <span className="truncate">{task.housekeeper_names.join(', ')}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => handleShareGallery(task, e)}
                          className={`${copiedTaskId === (task.task_id || task.id) ? 'text-green-600' : 'text-slate-400 hover:text-emerald-600'}`}
                          title="Partager la galerie (copier le lien)"
                          disabled={sharingTaskId === (task.task_id || task.id)}
                          data-testid={`share-gallery-btn-${task.task_id || task.id}`}
                        >
                          {sharingTaskId === (task.task_id || task.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : copiedTaskId === (task.task_id || task.id) ? (
                            <Check className="h-4 w-4" />
                          ) : (
                            <Share2 className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); downloadPdfReport(task); }}
                          className="text-slate-400 hover:text-red-600"
                          title="Télécharger le rapport PDF avec certificat"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); downloadAllPhotos(task); }}
                          className="text-slate-400 hover:text-blue-600"
                          title="Télécharger toutes les photos"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                        <Camera className="h-3 w-3" />
                        {task.photos_count || task.photos?.length || 0} photos
                      </span>
                      {task.time_spent && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-50 text-emerald-700 rounded-full text-xs font-medium">
                          <Clock className="h-3 w-3" />
                          {formatTimeSpent(task.time_spent)}
                        </span>
                      )}
                    </div>
                  </CardContent>

                  {/* Photo Preview Grid - Style Instagram/Pinterest - Format carré */}
                  <div className="relative aspect-square bg-slate-100 flex-shrink-0">
                    {displayPhotos.length === 1 && (
                      <img
                        src={getPhotoUrl(displayPhotos[0])}
                        alt="Photo"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                    
                    {displayPhotos.length === 2 && (
                      <div className="grid grid-cols-2 h-full gap-0.5">
                        {displayPhotos.map((photo, idx) => (
                          <img
                            key={`photo-${task.task_id}-${idx}`}
                            src={getPhotoUrl(photo)}
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        ))}
                      </div>
                    )}
                    
                    {displayPhotos.length >= 3 && (
                      <div className="grid grid-cols-2 h-full gap-0.5">
                        <img
                          src={getPhotoUrl(displayPhotos[0])}
                          alt="Photo 1"
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="grid grid-rows-2 gap-0.5 h-full">
                          <img
                            src={getPhotoUrl(displayPhotos[1])}
                            alt="Photo 2"
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                          <div className="relative">
                            <img
                              src={getPhotoUrl(displayPhotos[2])}
                              alt="Photo 3"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                            {remainingCount > 0 && (
                              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                <span className="text-white text-2xl font-bold">+{remainingCount}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-center">
                        <span className="text-white text-sm font-medium flex items-center gap-1 bg-black/30 px-3 py-1 rounded-full">
                          <Eye className="h-4 w-4" />
                          Voir les {task.photos_count || task.photos?.length || 0} photos
                        </span>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {!loading && gallery.total_pages > 1 && (
          <div className="flex items-center justify-center gap-2 py-6">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-slate-600 px-4">
              Page {currentPage} sur {gallery.total_pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(gallery.total_pages, p + 1))}
              disabled={currentPage === gallery.total_pages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-4xl p-0 bg-black/95 border-0">
          <DialogHeader className="absolute top-0 left-0 right-0 z-10 p-4 bg-gradient-to-b from-black/80 to-transparent">
            <DialogTitle className="text-white flex items-center justify-between">
              <span>
                {lightboxTask?.property_name} - Photo {lightboxIndex + 1}/{lightboxPhotos.length}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => downloadPdfReport(lightboxTask)}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Rapport PDF
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => downloadPhoto(lightboxPhotos[lightboxIndex], `${lightboxTask?.property_name}_photo${lightboxIndex + 1}.jpg`)}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Télécharger
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setLightboxOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="relative flex items-center justify-center min-h-[60vh]">
            {/* Navigation buttons */}
            {lightboxIndex > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 text-white hover:bg-white/20 z-10"
                onClick={() => setLightboxIndex(i => i - 1)}
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
            )}
            
            <img
              src={getPhotoUrl(lightboxPhotos[lightboxIndex])}
              alt={`Photo ${lightboxIndex + 1}`}
              className="max-h-[80vh] max-w-full object-contain"
            />
            
            {lightboxIndex < lightboxPhotos.length - 1 && (
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 text-white hover:bg-white/20 z-10"
                onClick={() => setLightboxIndex(i => i + 1)}
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            )}
          </div>

          {/* GPS Info Panel */}
          {lightboxTask && (
            <div className="p-3 bg-black/80 border-t border-white/20">
              {(() => {
                const taskId = lightboxTask.task_id || lightboxTask.id;
                const gpsInfo = getPhotoGpsInfo(taskId, lightboxPhotos[lightboxIndex]);
                const photoMeta = getPhotoMetadata(taskId, lightboxPhotos[lightboxIndex]);
                const isKeyboxPhoto = photoMeta?.is_keybox_photo;
                
                if (loadingMetadata) {
                  return <p className="text-white/60 text-sm">Chargement des infos GPS...</p>;
                }
                
                return (
                  <div className="space-y-1">
                    {/* Badge Photo Boîte à clés */}
                    {isKeyboxPhoto && (
                      <div className="flex items-center gap-2 text-amber-400 text-sm font-medium">
                        <span>🔑 Photo de certification (boîte à clés)</span>
                      </div>
                    )}
                    
                    {/* Info GPS */}
                    {(!gpsInfo || gpsInfo.status === 'no_gps') ? (
                      <p className="text-yellow-400 text-sm">⚠️ Pas de données GPS pour cette photo</p>
                    ) : gpsInfo.status === 'validated' && gpsInfo.validated ? (
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <span>✅ GPS vérifié</span>
                        <span className="text-white/60">|</span>
                        <span>📍 {gpsInfo.lat?.toFixed(5)}, {gpsInfo.lon?.toFixed(5)}</span>
                        {gpsInfo.distance_meters && (
                          <span className="text-white/60">({Math.round(gpsInfo.distance_meters)}m du logement)</span>
                        )}
                      </div>
                    ) : (gpsInfo.status === 'failed' || gpsInfo.validated === false) ? (
                      <div className="flex items-center gap-2 text-red-400 text-sm">
                        <span>❌ GPS NON CONFORME</span>
                        <span className="text-white/60">|</span>
                        <span>📍 {gpsInfo.lat?.toFixed(5)}, {gpsInfo.lon?.toFixed(5)}</span>
                        {gpsInfo.distance_meters && (
                          <span className="font-bold">({Math.round(gpsInfo.distance_meters)}m du logement - TROP LOIN)</span>
                        )}
                      </div>
                    ) : gpsInfo.lat && gpsInfo.lon ? (
                      <div className="flex items-center gap-2 text-blue-400 text-sm">
                        <span>📍 Position : {gpsInfo.lat?.toFixed(5)}, {gpsInfo.lon?.toFixed(5)}</span>
                        <span className="text-white/60">(non vérifié - FDM sans restriction GPS)</span>
                      </div>
                    ) : null}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Thumbnails */}
          <div className="p-4 bg-black/80 flex gap-2 overflow-x-auto">
            {lightboxPhotos.map((photo, idx) => (
              <div
                key={`thumb-${photo.substring(0, 20)}-${idx}`}
                className={`shrink-0 w-16 h-16 rounded cursor-pointer overflow-hidden ${
                  idx === lightboxIndex ? 'ring-2 ring-white' : 'opacity-50 hover:opacity-80'
                }`}
                onClick={() => setLightboxIndex(idx)}
              >
                <img
                  src={getPhotoUrl(photo)}
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PhotoGallery;
